import Head from "next/head";
import { motion } from "framer-motion";
import { Button } from "@/components/Button";
import { useRouter } from "next/router";
import { SEOMeta } from "@/templates/SEOMeta";
import {
  GiftIcon,
  UserGroupIcon,
  LinkIcon,
  TicketIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  SparklesIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import { SeoInternalLinks } from "@/components/SeoInternalLinks";
import { useTranslations } from "next-intl";
import { getI18nProps } from "@/utils/i18n";

export default function ReferralProgramSoftware({ seoMeta }) {
  const router = useRouter();
  const t = useTranslations("referralProgramSoftware");

  const primary = {
    title: t("seo.title"),
    description: t("seo.description"),
    keywords: t("seo.keywords"),
    url: "/referral-program-software",
  };

  const rewardModels = [
    {
      icon: GiftIcon,
      title: t("rewardModels.giveGet.title"),
      description: t("rewardModels.giveGet.description"),
    },
    {
      icon: CurrencyDollarIcon,
      title: t("rewardModels.cashCredits.title"),
      description: t("rewardModels.cashCredits.description"),
    },
    {
      icon: SparklesIcon,
      title: t("rewardModels.tiered.title"),
      description: t("rewardModels.tiered.description"),
    },
    {
      icon: AdjustmentsHorizontalIcon,
      title: t("rewardModels.custom.title"),
      description: t("rewardModels.custom.description"),
    },
  ];

  const steps = [
    {
      icon: RocketLaunchIcon,
      title: t("steps.launch.title"),
      description: t("steps.launch.description"),
    },
    {
      icon: LinkIcon,
      title: t("steps.share.title"),
      description: t("steps.share.description"),
    },
    {
      icon: ChartBarIcon,
      title: t("steps.trackReward.title"),
      description: t("steps.trackReward.description"),
    },
  ];

  const features = [
    {
      icon: TicketIcon,
      title: t("features.linksCodes.title"),
      description: t("features.linksCodes.description"),
    },
    {
      icon: ShieldCheckIcon,
      title: t("features.brandSafe.title"),
      description: t("features.brandSafe.description"),
    },
    {
      icon: ChartBarIcon,
      title: t("features.analytics.title"),
      description: t("features.analytics.description"),
    },
    {
      icon: UserGroupIcon,
      title: t("features.ambassadorFriendly.title"),
      description: t("features.ambassadorFriendly.description"),
    },
  ];

  const faqs = [
    { q: t("faqs.q1.q"), a: t("faqs.q1.a") },
    { q: t("faqs.q2.q"), a: t("faqs.q2.a") },
    { q: t("faqs.q3.q"), a: t("faqs.q3.a") },
    { q: t("faqs.q4.q"), a: t("faqs.q4.a") },
    { q: t("faqs.q5.q"), a: t("faqs.q5.a") },
    { q: t("faqs.q6.q"), a: t("faqs.q6.a") },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const appJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Afivio",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://afivio.com/referral-program-software",
    description: primary.description,
  };

  return (
      <>
        <SEOMeta
            title={primary.title}
            description={primary.description}
            keywords={primary.keywords}
            url={primary.url}
        />

        <Head>
          <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(appJsonLd) }}
          />
          <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
          />
        </Head>

        <div className="bg-gradient-to-b from-gray-50 to-white">
          {/* Hero */}
          <section className="pt-28 pb-20 px-6">
            <div className="max-w-7xl mx-auto text-center">
              <motion.h1
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent mb-6"
              >
                {t("hero.title")}
              </motion.h1>

              <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                  className="text-xl text-gray-600 max-w-3xl mx-auto mb-10"
              >
                {t("hero.subtitle")}
              </motion.p>

              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                    onClick={() => router.push("/signup")}
                    className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-4 rounded-xl hover:opacity-90 transition-opacity"
                >
                  {t("hero.primaryCta")}
                </Button>
                <a
                    href="/how-it-works"
                    className="inline-flex items-center justify-center px-8 py-4 rounded-xl border border-gray-200 text-gray-800 hover:bg-gray-50 transition-colors"
                >
                  {t("hero.secondaryCta")} <ArrowRightIcon className="w-5 h-5 ml-2" />
                </a>
              </div>

              <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <span className="inline-flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" />{" "}
                {t("hero.bullets.linksCodes")}
              </span>
                <span className="inline-flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" />{" "}
                  {t("hero.bullets.rewardsTiers")}
              </span>
                <span className="inline-flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" />{" "}
                  {t("hero.bullets.analytics")}
              </span>
              </div>
            </div>
          </section>

          {/* Reward models */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl font-bold mb-4">{t("rewardModelsSection.title")}</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  {t("rewardModelsSection.subtitle")}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {rewardModels.map((m, idx) => (
                    <motion.div
                        key={m.title}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.08 }}
                        className="bg-gray-50 border border-gray-100 p-8 rounded-2xl"
                    >
                      <m.icon className="w-11 h-11 text-blue-600 mb-4" />
                      <h3 className="text-xl font-semibold mb-2">{m.title}</h3>
                      <p className="text-gray-600">{m.description}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Steps */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl font-bold mb-4">{t("stepsSection.title")}</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  {t("stepsSection.subtitle")}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {steps.map((s, idx) => (
                    <motion.div
                        key={s.title}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100"
                    >
                      <div className="flex items-center mb-5">
                        <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mr-4">
                          <s.icon className="w-7 h-7 text-blue-600" />
                        </div>
                        <h3 className="text-2xl font-semibold">{s.title}</h3>
                      </div>
                      <p className="text-gray-600">{s.description}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl font-bold mb-4">{t("featuresSection.title")}</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  {t("featuresSection.subtitle")}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {features.map((f, idx) => (
                    <motion.div
                        key={f.title}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.07 }}
                        className="bg-gray-50 border border-gray-100 p-8 rounded-2xl"
                    >
                      <f.icon className="w-11 h-11 text-blue-600 mb-4" />
                      <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
                      <p className="text-gray-600">{f.description}</p>
                    </motion.div>
                ))}
              </div>

              <div className="mt-12 text-center">
                <a
                    href="/pricing"
                    className="inline-flex items-center px-6 py-3 rounded-xl bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  {t("featuresSection.pricingLink")}{" "}
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </a>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-4xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">{t("faq.title")}</h2>
                <p className="text-gray-600">{t("faq.subtitle")}</p>
              </div>

              <div className="space-y-4">
                {faqs.map((f, idx) => (
                    <motion.details
                        key={f.q}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        className="group bg-white border border-gray-100 rounded-2xl p-6"
                    >
                      <summary className="cursor-pointer list-none flex items-center justify-between">
                        <span className="text-lg font-semibold">{f.q}</span>
                        <span className="ml-4 text-gray-400 group-open:rotate-90 transition-transform">
                      ›
                    </span>
                      </summary>
                      <p className="mt-4 text-gray-600 leading-relaxed">{f.a}</p>
                    </motion.details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-24">
            <div className="max-w-5xl mx-auto px-6 text-center">
              <motion.div
                  initial={{ scale: 0.98, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  className="bg-gradient-to-r from-blue-600 to-green-500 p-12 rounded-3xl text-white shadow-xl"
              >
                <h2 className="text-4xl font-bold mb-5">{t("cta.title")}</h2>
                <p className="text-xl mb-8 max-w-2xl mx-auto">{t("cta.subtitle")}</p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button gray onClick={() => router.push("/signup")}>
                    {t("cta.primary")}
                  </Button>
                  <a
                      href="/how-it-works"
                      className="inline-flex items-center justify-center px-8 py-3 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15 transition-colors"
                  >
                    {t("cta.secondary")} <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </a>
                </div>
              </motion.div>
            </div>
          </section>

          <SeoInternalLinks />
        </div>
      </>
  );
}

export async function getStaticProps(ctx) {
  return getI18nProps(ctx);
}
